import {
  PropertyCreationFeature,
  HomeownerPropertyCreationSettings,
} from "@/types/organization";
import { PROTECTED_PATHS } from "@/router/protectedRoutes";
import { PROPERTY_CREATION_FEATURES } from "@/utils/constants";

/**
 * Checks if settings exist and have homeowner features configured
 * @param settings - The property creation settings
 * @returns true if settings are configured
 */
const hasConfiguredSettings = (
  settings: HomeownerPropertyCreationSettings | null
): boolean => {
  return settings !== null && settings.homeownerFeatures.length > 0;
};

/**
 * Checks if a specific feature is visible for the current user.
 * Returns true if no settings are configured (show all by default).
 * - Homeowners see features in `homeownerFeatures`
 * - Admins see features in `organizationFeatures`
 *
 * @param feature - The feature to check
 * @param settings - The property creation settings
 * @param isAdmin - Whether the current user is an admin
 * @returns true if the feature should be visible to the user
 */
export function isFeatureVisibleForUser(
  feature: PropertyCreationFeature,
  settings: HomeownerPropertyCreationSettings | null,
  isAdmin: boolean = false
): boolean {
  if (!hasConfiguredSettings(settings)) {
    return true;
  }

  if (isAdmin) {
    // Admins see features assigned to the organization
    return settings!.organizationFeatures.includes(feature);
  }

  // Homeowners see features assigned to them
  return settings!.homeownerFeatures.includes(feature);
}

/**
 * Checks if a section within the property form should be visible.
 * Used for sections like Property Features, Vendor Information, etc.
 *
 * @param sectionFeature - The feature key for the section
 * @param settings - The property creation settings
 * @param isAdmin - Whether the current user is an admin
 * @returns true if the section should be visible
 */
export function isSectionVisible(
  sectionFeature: PropertyCreationFeature,
  settings: HomeownerPropertyCreationSettings | null,
  isAdmin: boolean = false
): boolean {
  return isFeatureVisibleForUser(sectionFeature, settings, isAdmin);
}

/**
 * Checks if a value is considered "empty" (null, undefined, empty string, or 0 for numbers)
 * @param value - The value to check
 * @returns true if the value is empty
 */
const isEmptyValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  if (typeof value === "number" && value === 0) return true;
  return false;
};

/**
 * Gets the default value for a specific field from the organization settings.
 *
 * @param feature - The property creation feature (e.g., "PROPERTY_INFORMATION")
 * @param fieldName - The name of the field to get the default for
 * @param settings - The homeowner's property creation settings
 * @returns The default value if found, or undefined
 */
export function getDefaultValueForField<T = unknown>(
  feature: PropertyCreationFeature,
  fieldName: string,
  settings: HomeownerPropertyCreationSettings | null
): T | undefined {
  if (!settings?.defaultValues) return undefined;

  const featureDefaults = settings.defaultValues[feature];
  if (!featureDefaults) return undefined;

  return featureDefaults[fieldName] as T | undefined;
}

/**
 * Merges organization default values with existing form values.
 * Only applies default values for fields that are currently empty.
 *
 * @param formValues - The current form values (from store or initial state)
 * @param settings - The homeowner's property creation settings with default values
 * @returns Form values with defaults applied for empty fields
 *
 * @example
 * ```ts
 * const initialValues = applyDefaultValues(onboardPropertyData, settings);
 * // Returns form values with organization defaults for any empty fields
 * ```
 */
export function applyDefaultValues<T extends Record<string, unknown>>(
  formValues: T,
  settings: HomeownerPropertyCreationSettings | null
): T {
  if (!settings?.defaultValues) return formValues;

  const result = { ...formValues };

  // Iterate through all features and their default values
  Object.entries(settings.defaultValues).forEach(([, featureDefaults]) => {
    if (!featureDefaults) return;

    // Apply each default value if the current form value is empty
    Object.entries(featureDefaults).forEach(([fieldName, defaultValue]) => {
      if (fieldName in result && isEmptyValue(result[fieldName])) {
        (result as Record<string, unknown>)[fieldName] = defaultValue;
      }
    });
  });

  return result;
}

/**
 * Maps property creation features to their corresponding routes.
 * Used for navigating admins to specific property creation steps.
 */
const FEATURE_TO_ROUTE_MAP: Partial<Record<PropertyCreationFeature, string>> = {
  [PROPERTY_CREATION_FEATURES.SELECT_PROPERTY_TYPE]: PROTECTED_PATHS.NEW_PROPERTY,
  [PROPERTY_CREATION_FEATURES.EXISTING_PROPERTY_LISTINGS]: "", // Part of listing form (step 1)
  [PROPERTY_CREATION_FEATURES.PROPERTY_INFORMATION]: "", // Part of listing form (step 1)
  [PROPERTY_CREATION_FEATURES.PROPERTY_DETAILS]: "", // Part of listing form (step 1)
  [PROPERTY_CREATION_FEATURES.PROPERTY_FEATURES]: "", // Part of listing form (step 1)
  [PROPERTY_CREATION_FEATURES.PROPERTY_DESCRIPTION]: "", // Part of listing form (step 1)
  [PROPERTY_CREATION_FEATURES.VENDOR_INFORMATION]: "", // Part of listing form (step 1)
  [PROPERTY_CREATION_FEATURES.ADDITIONAL_INFORMATION]: "", // Part of listing form (step 1)
  [PROPERTY_CREATION_FEATURES.PROFESSIONAL_PHOTOS]: PROTECTED_PATHS.UPLOAD_PHOTOS,
  [PROPERTY_CREATION_FEATURES.SIGN_DOCUMENTS]: PROTECTED_PATHS.SIGN_DOCUMENTS,
  [PROPERTY_CREATION_FEATURES.UPLOAD_DOCUMENTS]: PROTECTED_PATHS.UPLOAD_PROPERTY_DOCUMENTS,
  [PROPERTY_CREATION_FEATURES.CREATE_CALENDAR_BLOCK]: PROTECTED_PATHS.CALENDAR_BLOCK,
};

/**
 * Order of property creation steps for determining which step comes first.
 * Lower index = earlier in the flow.
 */
const STEP_ORDER: PropertyCreationFeature[] = [
  PROPERTY_CREATION_FEATURES.SELECT_PROPERTY_TYPE,
  PROPERTY_CREATION_FEATURES.EXISTING_PROPERTY_LISTINGS,
  PROPERTY_CREATION_FEATURES.PROPERTY_INFORMATION,
  PROPERTY_CREATION_FEATURES.PROPERTY_DETAILS,
  PROPERTY_CREATION_FEATURES.PROPERTY_FEATURES,
  PROPERTY_CREATION_FEATURES.PROPERTY_DESCRIPTION,
  PROPERTY_CREATION_FEATURES.VENDOR_INFORMATION,
  PROPERTY_CREATION_FEATURES.ADDITIONAL_INFORMATION,
  PROPERTY_CREATION_FEATURES.PROFESSIONAL_PHOTOS,
  PROPERTY_CREATION_FEATURES.SIGN_DOCUMENTS,
  PROPERTY_CREATION_FEATURES.UPLOAD_DOCUMENTS,
  PROPERTY_CREATION_FEATURES.CREATE_CALENDAR_BLOCK,
];

/**
 * Features that are part of the listing form (step 1) rather than separate pages.
 */
const LISTING_FORM_FEATURES: PropertyCreationFeature[] = [
  PROPERTY_CREATION_FEATURES.EXISTING_PROPERTY_LISTINGS,
  PROPERTY_CREATION_FEATURES.PROPERTY_INFORMATION,
  PROPERTY_CREATION_FEATURES.PROPERTY_DETAILS,
  PROPERTY_CREATION_FEATURES.PROPERTY_FEATURES,
  PROPERTY_CREATION_FEATURES.PROPERTY_DESCRIPTION,
  PROPERTY_CREATION_FEATURES.VENDOR_INFORMATION,
  PROPERTY_CREATION_FEATURES.ADDITIONAL_INFORMATION,
];

/**
 * Gets the route for the first pending organization step.
 * If the first pending step is part of the listing form, navigates to the property edit page.
 *
 * @param pendingOrgSteps - Array of pending organization-assigned feature keys
 * @param propertyId - The property ID (used for edit routes)
 * @param propertyType - The property type (SINGLE_FAMILY or MULTI_FAMILY)
 * @returns The route to navigate to, or null if no pending steps
 */
export function getFirstPendingOrgStepRoute(
  pendingOrgSteps: string[] | undefined,
  propertyId: string,
  propertyType?: "SINGLE_FAMILY" | "MULTI_FAMILY"
): string | null {
  if (!pendingOrgSteps || pendingOrgSteps.length === 0) {
    return null;
  }

  // Sort pending steps by their order in the flow
  const sortedSteps = [...pendingOrgSteps].sort((a, b) => {
    const indexA = STEP_ORDER.indexOf(a as PropertyCreationFeature);
    const indexB = STEP_ORDER.indexOf(b as PropertyCreationFeature);
    return indexA - indexB;
  });

  let firstPendingStep = sortedSteps[0] as PropertyCreationFeature;

  // If SELECT_PROPERTY_TYPE is pending but property already has a type,
  // skip to the next pending step (property type was already completed)
  if (
    firstPendingStep === PROPERTY_CREATION_FEATURES.SELECT_PROPERTY_TYPE &&
    propertyType
  ) {
    // Property already has a type, skip this step
    if (sortedSteps.length > 1) {
      firstPendingStep = sortedSteps[1] as PropertyCreationFeature;
    } else {
      // No more pending steps
      return null;
    }
  }

  // If the first pending step is part of the listing form, navigate to edit property page
  if (LISTING_FORM_FEATURES.includes(firstPendingStep)) {
    const editRoute =
      propertyType === "MULTI_FAMILY"
        ? PROTECTED_PATHS.EDIT_MULTI_FAMILY_PROPERTY
        : PROTECTED_PATHS.EDIT_SINGLE_FAMILY_PROPERTY;
    return editRoute.replace(":propertyId", propertyId);
  }

  // Otherwise, get the route for the specific step
  const route = FEATURE_TO_ROUTE_MAP[firstPendingStep];
  if (route) {
    return route;
  }

  return null;
}

/**
 * Checks if a property has any pending organization-assigned steps.
 *
 * @param pendingOrgSteps - Array of pending organization-assigned feature keys
 * @returns true if there are pending organization steps
 */
export function hasPendingOrgSteps(
  pendingOrgSteps: string[] | undefined
): boolean {
  return !!pendingOrgSteps && pendingOrgSteps.length > 0;
}

/**
 * Maps a property creation feature to its corresponding navigation path.
 * Used for feature-based navigation after calling completeFeatures API.
 *
 * @param feature - The feature name (e.g., "PROFESSIONAL_PHOTOS", "SIGN_DOCUMENTS")
 * @param propertyId - Optional property ID for routes that require it
 * @param propertyType - Optional property type for determining edit routes
 * @returns The navigation path for the feature, or null if not found
 */
export function getPathForFeature(
  feature: string | null | undefined,
  propertyId?: string,
  propertyType?: "SINGLE_FAMILY" | "MULTI_FAMILY"
): string | null {
  if (!feature) return null;

  // Features that are part of the listing form (CREATE_LISTING step)
  const listingFormFeatures: string[] = [
    PROPERTY_CREATION_FEATURES.SELECT_PROPERTY_TYPE,
    PROPERTY_CREATION_FEATURES.EXISTING_PROPERTY_LISTINGS,
    PROPERTY_CREATION_FEATURES.PROPERTY_INFORMATION,
    PROPERTY_CREATION_FEATURES.PROPERTY_DETAILS,
    PROPERTY_CREATION_FEATURES.PROPERTY_FEATURES,
    PROPERTY_CREATION_FEATURES.PROPERTY_DESCRIPTION,
    PROPERTY_CREATION_FEATURES.VENDOR_INFORMATION,
    PROPERTY_CREATION_FEATURES.ADDITIONAL_INFORMATION,
  ];

  // If the feature is part of the listing form, navigate to edit property page
  if (listingFormFeatures.includes(feature) && propertyId) {
    const editRoute =
      propertyType === "MULTI_FAMILY"
        ? PROTECTED_PATHS.EDIT_MULTI_FAMILY_PROPERTY
        : PROTECTED_PATHS.EDIT_SINGLE_FAMILY_PROPERTY;
    return editRoute.replace(":propertyId", propertyId);
  }

  // Map features to their dedicated pages
  const featurePathMap: Record<string, string> = {
    [PROPERTY_CREATION_FEATURES.PROFESSIONAL_PHOTOS]: PROTECTED_PATHS.UPLOAD_PHOTOS,
    [PROPERTY_CREATION_FEATURES.SIGN_DOCUMENTS]: PROTECTED_PATHS.SIGN_DOCUMENTS,
    [PROPERTY_CREATION_FEATURES.UPLOAD_DOCUMENTS]: PROTECTED_PATHS.UPLOAD_PROPERTY_DOCUMENTS,
    [PROPERTY_CREATION_FEATURES.CREATE_CALENDAR_BLOCK]: PROTECTED_PATHS.CALENDAR_BLOCK,
  };

  return featurePathMap[feature] || null;
}

/**
 * Gets all features up to and including the specified feature that are assigned to the current user.
 * Used when completing a feature to ensure all preceding features are also marked complete.
 * This handles the case where a property was created before the features API was implemented.
 *
 * @param currentFeature - The feature being completed
 * @param settings - The property creation settings (to determine user's assigned features)
 * @param isAdmin - Whether the current user is an admin
 * @returns Array of feature names to mark as complete
 */
export function getFeaturesUpToAndIncluding(
  currentFeature: string,
  settings: HomeownerPropertyCreationSettings | null,
  isAdmin: boolean = false
): string[] {
  const currentFeatureIndex = STEP_ORDER.indexOf(currentFeature as PropertyCreationFeature);

  if (currentFeatureIndex === -1) {
    // Feature not found in order, just return the current feature
    return [currentFeature];
  }

  // Get all features up to and including the current feature
  const featuresUpToCurrent = STEP_ORDER.slice(0, currentFeatureIndex + 1);

  // Filter to only include features assigned to the current user
  return featuresUpToCurrent.filter((feature) =>
    isFeatureVisibleForUser(feature, settings, isAdmin)
  );
}

/**
 * The subset of the current-step API response that describes the current
 * user's personal position in the flow. Kept structural so it applies to both
 * `getCurrentStep` and `completeFeatures` responses.
 */
type AdminSanitizableStepData = {
  myFeatures?: string[];
  myCompletedFeatures?: string[];
  myRemainingFeatures?: string[];
  nextFeature?: string | null;
  allMyFeaturesComplete?: boolean;
};

/**
 * Enforces the invariant that document signing always belongs to the
 * homeowner, never an admin — even when the org "handles all" property
 * creation. The backend snapshot can still surface SIGN_DOCUMENTS in an admin's
 * feature set, which would show the step in the progress bar and route the
 * admin onto (and flash through) the sign-documents screen. Stripping it here,
 * at the single point where step data enters the app, keeps every consumer
 * (stepper display and step-to-step navigation) consistent.
 *
 * No-op for non-admins, so homeowners still sign their own documents.
 *
 * @param stepData - The current-step response to sanitize
 * @param isAdmin - Whether the current user is an admin acting on behalf
 * @returns The step data with SIGN_DOCUMENTS removed from the admin's features
 */
export function stripSignDocumentsForAdmin<T extends AdminSanitizableStepData>(
  stepData: T,
  isAdmin: boolean
): T {
  if (!isAdmin || !stepData) return stepData;

  const withoutSignDocuments = (features?: string[]) =>
    features?.filter(
      (feature) => feature !== PROPERTY_CREATION_FEATURES.SIGN_DOCUMENTS
    );

  const myRemainingFeatures = withoutSignDocuments(stepData.myRemainingFeatures);

  // If the snapshot pointed the admin at SIGN_DOCUMENTS, advance to their next
  // remaining feature (in flow order) instead of the homeowner-only step.
  let nextFeature = stepData.nextFeature;
  if (nextFeature === PROPERTY_CREATION_FEATURES.SIGN_DOCUMENTS) {
    nextFeature =
      STEP_ORDER.find((feature) => myRemainingFeatures?.includes(feature)) ??
      null;
  }

  return {
    ...stepData,
    myFeatures: withoutSignDocuments(stepData.myFeatures),
    myCompletedFeatures: withoutSignDocuments(stepData.myCompletedFeatures),
    myRemainingFeatures,
    nextFeature,
    allMyFeaturesComplete: (myRemainingFeatures?.length ?? 0) === 0,
  };
}
